import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PlayerSearch from './PlayerSearch';
import PlayerGameLogs from './PlayerGameLogs';
import NBATimeMachine from './components/NBATimeMachine';
import './PlayerLogs.css';

// Get the API base URL (same as api.js)
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8000';
    }
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const PlayerLogs = () => {
    const { playerId } = useParams();
    const navigate = useNavigate();
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('time_machine'); // 'time_machine' or 'classic'
    const [selectedSeason, setSelectedSeason] = useState('2014-15');
    const [selectedSeasonType, setSelectedSeasonType] = useState('Regular Season + Playoffs');

    // If we have a playerId in the URL, fetch player info
    useEffect(() => {
        if (playerId) {
            fetchPlayerInfo(parseInt(playerId));
        }
    }, [playerId]);

    const fetchPlayerInfo = async (id) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/dev/player-logs/player/${id}`);
            if (!response.ok) {
                // Check if we're getting HTML instead of JSON (common in production)
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    throw new Error('API server not available. Please check if the backend is running.');
                }
                throw new Error(`Player not found: ${response.statusText}`);
            }
            
            const playerData = await response.json();
            setSelectedPlayer(playerData);
        } catch (err) {
            // Handle JSON parsing errors specifically
            if (err.name === 'SyntaxError' && err.message.includes('Unexpected token')) {
                setError('API server not available. Please check if the backend is running.');
            } else {
                setError(err.message);
            }
            console.error('Error fetching player info:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePlayerSelect = (player) => {
        setSelectedPlayer(player);
        setError(null);
        // Update URL to include player ID
        navigate(`/dev/player-logs/${player.id}`);
    };

    const handleBackToSearch = () => {
        setSelectedPlayer(null);
        setError(null);
        navigate('/dev/player-logs');
    };

    const handleSeasonChange = (season, seasonType) => {
        setSelectedSeason(season);
        setSelectedSeasonType(seasonType);
    };

    if (loading) {
        return (
            <div className="player-logs-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading player data...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="player-logs-container">
                <div className="error-message">
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button onClick={handleBackToSearch} className="back-button">
                        Back to Search
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="player-logs-container">
            <div className="player-logs-header">
                <h1>Player Game Logs</h1>
                <p className="dev-notice">Development Feature - Direct URL Access Only</p>
            </div>

            {!selectedPlayer ? (
                <PlayerSearch 
                    onPlayerSelect={handlePlayerSelect}
                    onSeasonChange={handleSeasonChange}
                    selectedSeason={selectedSeason}
                    selectedSeasonType={selectedSeasonType}
                />
            ) : (
                <div className="player-content">
                    <div className="player-header">
                        <button onClick={handleBackToSearch} className="back-button">
                            ← Back to Search
                        </button>
                        <div className="player-info">
                            <h2>{selectedPlayer.name}</h2>
                            {selectedPlayer.team && selectedPlayer.team.full_name && (
                                <p className="player-team">{selectedPlayer.team.full_name}</p>
                            )}
                            {selectedPlayer.position && (
                                <p className="player-position">{selectedPlayer.position}</p>
                            )}
                        </div>
                        <div className="view-mode-toggle">
                            <button 
                                className={`mode-btn ${viewMode === 'time_machine' ? 'active' : ''}`}
                                onClick={() => setViewMode('time_machine')}
                            >
                                🏀 NBA Time Machine
                            </button>
                            <button 
                                className={`mode-btn ${viewMode === 'classic' ? 'active' : ''}`}
                                onClick={() => setViewMode('classic')}
                            >
                                📊 Classic View
                            </button>
                        </div>
                    </div>
                    
                    {viewMode === 'time_machine' ? (
                        <NBATimeMachine 
                            player={selectedPlayer} 
                            season={selectedSeason}
                            seasonType={selectedSeasonType}
                            onGameSelect={(game) => {
                                console.log('Game selected for watchlist:', game);
                                // Future: Add to watchlist functionality
                            }}
                        />
                    ) : (
                        <PlayerGameLogs player={selectedPlayer} onPlayerSelect={handlePlayerSelect} />
                    )}
                </div>
            )}
        </div>
    );
};

export default PlayerLogs; 